import React, { useState, useEffect, useRef } from 'react';
import { PrivateLayout, PV_THEME } from '../PrivateLayout';

/*──────────────────────────────────────────────────────────────
  TRUSTFRAME SETTLEMENT CALCULATOR (port, on-brand)
  Ported from trustframe-calculator.tsx — re-skinned in the
  current TrustFrame Private View aesthetic (dark + orange).
──────────────────────────────────────────────────────────────*/

const T = PV_THEME;
const ACCENT = T.accent;        // orange / rust
const ACCENT_INK = T.accentInk; // ink-on-accent
const BG = T.bg;
const BG_INK = T.bgInk;         // deepest panel bg
const SURFACE = T.surface;      // #111116
const BORDER = T.border;        // #2a2a30
const BORDER_FAINT = T.borderFaint;
const TEXT = T.text;
const TEXT_MUTED = T.textMuted;
const TEXT_FAINT = T.textFaint;

const TIERS = [
  { id: 'emerging', label: 'EMERGING', mult: 1.0, tag: 'Early career — building recognition', dot: '#A47FDB' },
  { id: 'mid', label: 'MID-CAREER', mult: 1.8, tag: 'Gallery represented — growing market', dot: '#4da6ff' },
  { id: 'established', label: 'ESTABLISHED', mult: 3.2, tag: 'Museum collected — auction history', dot: '#00d68f' },
  { id: 'bluechip', label: 'BLUE CHIP', mult: 6.0, tag: 'Institutional demand — global market', dot: ACCENT },
];

const WORKS = [
  { id: 1, title: 'The Last Say So', price: 6000 },
  { id: 2, title: 'Untitled II', price: 2200 },
  { id: 3, title: 'Untitled III', price: 1600 },
  { id: 4, title: 'Untitled IV', price: 1500 },
  { id: 5, title: 'Untitled V', price: 2200 },
  { id: 6, title: 'The Grand Statement', price: 15000 },
];

const STAGES = [
  { id: 'listed', n: '01', label: 'Listed', desc: 'Work catalogued — Xero draft invoice created', xero: 'DRAFT', logistics: 'At Gallery', chip: true, notify: 'All parties notified: work available for acquisition' },
  { id: 'reserved', n: '02', label: 'Reserved', desc: 'Collector committed — Xero invoice sent, 20% deposit tracked', xero: 'SENT', logistics: 'At Gallery — hold', chip: true, notify: 'Artist + Gallery + TrustFrame: deposit confirmed in escrow' },
  { id: 'in-transit', n: '03', label: 'In Transit', desc: 'Full payment received — work in transit, chip tracking live', xero: 'PAID', logistics: 'In Transit', chip: true, notify: 'Live GPS: all parties tracking shipment in real-time' },
  { id: 'delivered', n: '04', label: 'Delivered', desc: 'Chip confirms arrival — collector inspection period', xero: 'PAID', logistics: 'At Collector', chip: true, notify: 'Artist + Gallery + TrustFrame: artwork received, inspection open' },
  { id: 'verified', n: '05', label: 'Verified', desc: 'All parties confirm — simultaneous fund release triggered', xero: 'RECONCILED', logistics: 'Final Location', chip: false, notify: 'SIMULTANEOUS: Artist + Gallery paid at same moment, TF fee retained' },
];

const fmt = (n) => '£' + Math.round(n).toLocaleString('en-GB');

function Anim({ value }) {
  const [d, setD] = useState(value);
  const r = useRef();
  useEffect(() => {
    const s = d;
    const e = value;
    if (s === e) return;
    let t0 = null;
    const tick = (ts) => {
      if (!t0) t0 = ts;
      let p = Math.min((ts - t0) / 350, 1);
      p = 1 - (1 - p) ** 3;
      setD(Math.round(s + (e - s) * p));
      if (p < 1) r.current = requestAnimationFrame(tick);
    };
    r.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(r.current);
  }, [value]);
  return <>{fmt(d)}</>;
}

function Panel({ children, label, accent, span }) {
  return (
    <div
      style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        position: 'relative',
        gridColumn: span ? `span ${span}` : undefined,
        borderRadius: 6,
      }}
    >
      {label && (
        <div style={{ padding: '10px 16px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          {accent && <div style={{ width: 6, height: 6, background: accent, borderRadius: 1 }} />}
          <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.25em', textTransform: 'uppercase', color: TEXT_MUTED, fontFamily: 'monospace' }}>{label}</span>
        </div>
      )}
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

function SplitBar({ artist, gallery, fee, total }) {
  const aP = (artist / total) * 100;
  const gP = (gallery / total) * 100;
  const fP = (fee / total) * 100;
  return (
    <div>
      <div style={{ display: 'flex', height: 5, gap: 2 }}>
        <div style={{ width: `${aP}%`, background: '#00d68f', transition: 'width 0.5s ease' }} />
        <div style={{ width: `${gP}%`, background: '#4da6ff', transition: 'width 0.5s ease' }} />
        <div style={{ width: `${fP}%`, background: ACCENT, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        {[
          { l: 'Artist', p: aP, c: '#00d68f' },
          { l: 'Gallery', p: gP, c: '#4da6ff' },
          { l: 'TrustFrame', p: fP, c: ACCENT },
        ].map((x) => (
          <span key={x.l} style={{ fontSize: 8, fontFamily: 'monospace', color: x.c, fontWeight: 600, letterSpacing: '0.1em' }}>
            {x.l} {x.p.toFixed(1)}%
          </span>
        ))}
      </div>
    </div>
  );
}

function EscrowPipeline({ stage, amounts }) {
  const idx = STAGES.findIndex((s) => s.id === stage);
  const cs = STAGES[idx];
  const chipPos = {
    'listed': { loc: 'Mayfair Gallery, London W1', coords: '51.5074°N, 0.1278°W' },
    'reserved': { loc: 'Mayfair Gallery, London W1 — HOLD', coords: '51.5074°N, 0.1278°W' },
    'in-transit': { loc: 'In Transit — Art Logistics Co.', coords: '51.4700°N, 0.2100°W' },
    'delivered': { loc: 'Collector Residence, Marylebone W1', coords: '51.5225°N, 0.1553°W' },
    'verified': { loc: 'Final Location Confirmed', coords: '51.5225°N, 0.1553°W' },
  }[stage];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, padding: '10px 12px', background: BG_INK, border: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, background: '#13B5EA', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: ACCENT_INK, fontFamily: 'monospace' }}>X</span>
          </div>
          <div>
            <div style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.15em', color: TEXT_MUTED, fontFamily: 'monospace' }}>XERO API</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: TEXT, fontFamily: 'monospace', marginTop: 1 }}>INV-{cs.xero}</div>
          </div>
          <div
            style={{
              marginLeft: 'auto',
              padding: '3px 8px',
              background: cs.xero === 'RECONCILED' ? 'rgba(0,214,143,0.15)' : cs.xero === 'PAID' ? 'rgba(19,181,234,0.15)' : 'rgba(245,166,35,0.1)',
              border: `1px solid ${cs.xero === 'RECONCILED' ? 'rgba(0,214,143,0.4)' : cs.xero === 'PAID' ? 'rgba(19,181,234,0.4)' : 'rgba(245,166,35,0.3)'}`,
              fontSize: 8,
              fontWeight: 600,
              fontFamily: 'monospace',
              letterSpacing: '0.12em',
              color: cs.xero === 'RECONCILED' ? '#00d68f' : cs.xero === 'PAID' ? '#13B5EA' : ACCENT,
            }}
          >
            {cs.xero}
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 200, padding: '10px 12px', background: BG_INK, border: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, background: cs.chip ? '#00d68f' : TEXT_FAINT, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 10, color: ACCENT_INK }}>◉</span>
          </div>
          <div>
            <div style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.15em', color: TEXT_MUTED, fontFamily: 'monospace' }}>LOGISTICS CHIP</div>
            <div style={{ fontSize: 9, fontWeight: 500, color: TEXT, fontFamily: 'monospace', marginTop: 1 }}>{cs.logistics}</div>
          </div>
        </div>
      </div>

      <div style={{ padding: '10px 14px', background: BG_INK, border: `1px solid ${BORDER}`, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10, color: stage === 'in-transit' ? '#00d68f' : ACCENT }}>◉</span>
          <span style={{ fontSize: 10, color: TEXT_MUTED, fontFamily: 'monospace', letterSpacing: '0.06em' }}>{chipPos.loc}</span>
        </div>
        <span style={{ fontSize: 9, color: TEXT_FAINT, fontFamily: 'monospace' }}>{chipPos.coords}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0, border: `1px solid ${BORDER}` }}>
        {STAGES.map((s, i) => {
          const active = i <= idx;
          const current = i === idx;
          return (
            <div
              key={s.id}
              style={{
                padding: '12px 8px',
                background: current ? BG_INK : active ? 'rgba(245,166,35,0.05)' : 'transparent',
                borderRight: i < 4 ? `1px solid ${BORDER}` : 'none',
                transition: 'all 0.4s ease',
                position: 'relative',
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 300, color: current ? ACCENT : active ? TEXT_MUTED : TEXT_FAINT, fontFamily: 'monospace', lineHeight: 1, marginBottom: 4 }}>{s.n}</div>
              <div style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: current ? TEXT : active ? TEXT_MUTED : TEXT_FAINT, fontFamily: 'monospace', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 8, color: current ? TEXT_MUTED : TEXT_FAINT, fontFamily: 'sans-serif', lineHeight: 1.4, marginBottom: 4 }}>{s.desc}</div>
              {active && amounts && (
                <div style={{ marginTop: 4, fontSize: 11, fontWeight: 600, color: current ? ACCENT : TEXT_MUTED, fontFamily: 'monospace' }}>
                  {i === 0 ? fmt(amounts.gallery) : i === 1 ? fmt(amounts.deposit) : i < 4 ? fmt(amounts.gallery) : <span style={{ color: '#00d68f' }}>RELEASED</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 12, padding: 12, background: BG_INK, border: `1px solid ${BORDER}` }}>
        <div style={{ fontSize: 8, fontWeight: 600, letterSpacing: '0.2em', color: ACCENT, fontFamily: 'monospace', marginBottom: 10 }}>
          ./TRI-PARTY-NOTIFICATIONS — SIMULTANEOUS
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 70 }}>
            {['Artist', 'Gallery', 'TF'].map((p) => (
              <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00d68f', boxShadow: '0 0 6px #00d68f' }} />
                <span style={{ fontSize: 8, color: TEXT_MUTED, fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.1em' }}>{p}</span>
              </div>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 220, padding: '10px 12px', background: SURFACE, border: `1px solid ${BORDER}` }}>
            <div style={{ fontSize: 10, color: TEXT, fontFamily: 'sans-serif', lineHeight: 1.55 }}>{cs.notify}</div>
            <div style={{ fontSize: 8, color: TEXT_FAINT, fontFamily: 'monospace', marginTop: 6, letterSpacing: '0.1em' }}>
              [ .all-parties-notified-simultaneously. ]
            </div>
          </div>
        </div>

        {stage === 'verified' && (
          <div style={{ marginTop: 10, padding: 12, background: 'rgba(0,214,143,0.05)', border: '1px solid rgba(0,214,143,0.3)' }}>
            <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.18em', color: '#00d68f', fontFamily: 'monospace', marginBottom: 10 }}>
              ✦ SIMULTANEOUS FUND RELEASE EXECUTED
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { party: 'Artist', val: amounts.artist, color: '#00d68f' },
                { party: 'Gallery', val: amounts.galleryS, color: '#4da6ff' },
                { party: 'TrustFrame', val: amounts.fee, color: ACCENT },
              ].map((x) => (
                <div key={x.party} style={{ flex: '1 1 110px', padding: 10, border: `1px solid ${x.color}55`, background: `${x.color}0d`, textAlign: 'center' }}>
                  <div style={{ fontSize: 8, color: x.color, fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.15em' }}>{x.party.toUpperCase()}</div>
                  <div style={{ fontSize: 18, color: TEXT, fontFamily: 'monospace', marginTop: 4, letterSpacing: '0.03em' }}>{fmt(x.val)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 4 }}>
                    <div style={{ width: 5, height: 5, background: '#00d68f', borderRadius: '50%', boxShadow: '0 0 4px #00d68f' }} />
                    <span style={{ fontSize: 8, color: '#00d68f', fontFamily: 'monospace', letterSpacing: '0.15em' }}>PAID</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const sl = { fontSize: 10, color: TEXT_MUTED, fontFamily: 'monospace', fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' };
const rl = { fontSize: 8, color: TEXT_FAINT, fontFamily: 'monospace', letterSpacing: '0.08em' };

export default function CalculatorSection() {
  const [price, setPrice] = useState(6000);
  const [tier, setTier] = useState('emerging');
  const [markup, setMarkup] = useState(100);
  const [tfPct, setTfPct] = useState(5);
  const [stage, setStage] = useState('listed');
  const [selWork, setSelWork] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const t = TIERS.find((x) => x.id === tier);
  const galleryPrice = Math.round(price * (1 + markup / 100) * t.mult);
  const tfFee = Math.round(galleryPrice * tfPct / 100);
  const net = galleryPrice - tfFee;
  const artistShare = Math.round(net * 0.5);
  const galleryShare = net - artistShare;
  const deposit = Math.round(galleryPrice * 0.2);
  const amounts = { gallery: galleryPrice, deposit, artist: artistShare, galleryS: galleryShare, fee: tfFee };

  return (
    <PrivateLayout eyebrowLabel="Settlement Calculator" testid="pv-calculator">
      <div data-testid="calculator-canvas">
        {/* Hero */}
        <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
          <p className="font-mono uppercase mb-3" style={{ fontSize: 10, letterSpacing: '0.4em', color: ACCENT }}>
            [ .VII. .CALCULATOR. ]
          </p>
          <h1
            className="font-machina font-bold uppercase mb-4"
            style={{ fontSize: 'clamp(30px,5vw,52px)', lineHeight: 0.95, color: TEXT }}
          >
            Settlement<br />Calculator.
          </h1>
          <p className="font-mono leading-[1.8] max-w-2xl mb-2" style={{ fontSize: 13, color: TEXT_MUTED }}>
            50/50 artist-gallery split after TrustFrame escrow. Xero API tracks collector payments from invoice to reconciliation. Logistics chip GPS tracks works from gallery to final location. All parties receive identical notifications simultaneously — funds release to artist and gallery at the same moment.
          </p>
          <div className="mt-4 inline-flex flex-wrap items-center gap-3 font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.2em', color: TEXT_FAINT }}>
            <span style={{ padding: '4px 10px', border: `1px solid ${BORDER}`, color: '#13B5EA' }}>XERO API</span>
            <span style={{ padding: '4px 10px', border: `1px solid ${BORDER}`, color: '#00d68f' }}>LOGISTICS CHIP</span>
            <span style={{ padding: '4px 10px', border: `1px solid ${BORDER}`, color: ACCENT }}>v3.0</span>
          </div>
        </section>

        {/* Grid */}
        <section className="relative z-10 max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
            <Panel label="Helena Foster — Sample Works" accent="#4da6ff">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 6 }}>
                {WORKS.map((w) => (
                  <button
                    key={w.id}
                    data-testid={`work-${w.id}`}
                    onClick={() => {
                      setSelWork(w.id);
                      setPrice(w.price);
                    }}
                    style={{
                      background: selWork === w.id ? ACCENT : 'transparent',
                      border: `1px solid ${selWork === w.id ? ACCENT : BORDER}`,
                      padding: '9px 8px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.25s ease',
                    }}
                  >
                    <div style={{ fontSize: 9, fontWeight: 600, color: selWork === w.id ? ACCENT_INK : TEXT, fontFamily: 'monospace', lineHeight: 1.3 }}>{w.title}</div>
                    <div style={{ fontSize: 8, color: selWork === w.id ? 'rgba(10,10,11,0.7)' : TEXT_FAINT, fontFamily: 'monospace', marginTop: 4, letterSpacing: '0.08em' }}>{fmt(w.price)}</div>
                  </button>
                ))}
              </div>
            </Panel>

            <Panel label="Gallery Sale Price" accent={ACCENT}>
              <div style={{ fontFamily: 'monospace', fontSize: 'clamp(36px,5vw,48px)', fontWeight: 400, color: TEXT, lineHeight: 1, marginBottom: 6, letterSpacing: '0.02em', textShadow: '0 0 16px rgba(245,166,35,0.25)' }}>
                <Anim value={galleryPrice} />
              </div>
              <div style={{ fontSize: 9, color: TEXT_MUTED, fontFamily: 'monospace', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 14 }}>
                {t.label} · ×{t.mult} · {markup}% markup
              </div>
              <SplitBar artist={artistShare} gallery={galleryShare} fee={tfFee} total={galleryPrice} />
            </Panel>

            <Panel label="Studio Price" accent="#00d68f">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <input
                  type="number"
                  data-testid="studio-price-input"
                  value={price}
                  onChange={(e) => {
                    setPrice(Number(e.target.value) || 0);
                    setSelWork(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${BORDER}`,
                    padding: '8px 12px',
                    fontFamily: 'monospace',
                    fontSize: 16,
                    fontWeight: 600,
                    color: TEXT,
                    width: 140,
                    outline: 'none',
                  }}
                />
                <span style={{ fontSize: 9, color: TEXT_FAINT, fontFamily: 'monospace', letterSpacing: '0.15em' }}>GBP</span>
              </div>
              <input
                type="range"
                min={500}
                max={150000}
                step={500}
                value={price}
                onChange={(e) => {
                  setPrice(Number(e.target.value));
                  setSelWork(null);
                }}
                style={{ width: '100%', accentColor: ACCENT, height: 2, cursor: 'pointer' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={rl}>£500</span>
                <span style={rl}>£150,000</span>
              </div>
            </Panel>

            <Panel label="Settlement Distribution" accent={ACCENT}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                {[
                  { l: 'ARTIST', v: artistShare, s: '50% net', c: '#00d68f' },
                  { l: 'GALLERY', v: galleryShare, s: '50% net', c: '#4da6ff' },
                  { l: 'TRUSTFRAME', v: tfFee, s: `${tfPct}% escrow`, c: ACCENT },
                ].map((x) => (
                  <div key={x.l} style={{ border: `1px solid ${BORDER}`, padding: '12px 6px', textAlign: 'center', background: BG_INK }}>
                    <div style={{ fontSize: 7, fontWeight: 600, letterSpacing: '0.22em', color: x.c, fontFamily: 'monospace' }}>{x.l}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 18, color: TEXT, marginTop: 5, fontWeight: 500 }}>
                      <Anim value={x.v} />
                    </div>
                    <div style={{ fontSize: 8, color: TEXT_FAINT, fontFamily: 'monospace', marginTop: 3, letterSpacing: '0.1em' }}>{x.s}</div>
                    {stage === 'verified' && x.l !== 'TRUSTFRAME' && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, marginTop: 5 }}>
                        <div style={{ width: 4, height: 4, background: '#00d68f', borderRadius: '50%', boxShadow: '0 0 4px #00d68f' }} />
                        <span style={{ fontSize: 7, color: '#00d68f', fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.15em' }}>PAID</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Panel>

            <Panel label="Artist Status Tier" accent="#A47FDB">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 6 }}>
                {TIERS.map((x) => (
                  <button
                    key={x.id}
                    data-testid={`tier-${x.id}`}
                    onClick={() => setTier(x.id)}
                    style={{
                      background: tier === x.id ? BG_INK : 'transparent',
                      border: `1px solid ${tier === x.id ? x.dot : BORDER}`,
                      padding: '10px 9px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.25s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 6, height: 6, background: x.dot, borderRadius: 1, opacity: tier === x.id ? 1 : 0.4 }} />
                      <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', color: tier === x.id ? TEXT : TEXT_MUTED, fontFamily: 'monospace' }}>{x.label}</span>
                    </div>
                    <div style={{ fontSize: 8, color: tier === x.id ? TEXT_MUTED : TEXT_FAINT, fontFamily: 'sans-serif', marginTop: 4, lineHeight: 1.4 }}>{x.tag}</div>
                    <div style={{ fontSize: 8, color: tier === x.id ? x.dot : TEXT_FAINT, fontFamily: 'monospace', marginTop: 4, letterSpacing: '0.08em' }}>×{x.mult} factor</div>
                  </button>
                ))}
              </div>
            </Panel>

            <Panel label="Adjustments" accent="#13B5EA">
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={sl}>Gallery Markup</span>
                  <span style={{ ...sl, fontSize: 14, color: TEXT, fontWeight: 600 }}>{markup}%</span>
                </div>
                <input
                  type="range"
                  min={20}
                  max={300}
                  step={5}
                  value={markup}
                  onChange={(e) => setMarkup(Number(e.target.value))}
                  data-testid="markup-input"
                  style={{ width: '100%', accentColor: ACCENT, height: 2, cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={rl}>20%</span>
                  <span style={rl}>300%</span>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={sl}>TF Escrow Fee</span>
                  <span style={{ ...sl, fontSize: 14, color: TEXT, fontWeight: 600 }}>{tfPct}%</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={15}
                  step={0.5}
                  value={tfPct}
                  onChange={(e) => setTfPct(Number(e.target.value))}
                  data-testid="tfpct-input"
                  style={{ width: '100%', accentColor: ACCENT, height: 2, cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={rl}>1%</span>
                  <span style={rl}>15%</span>
                </div>
              </div>
            </Panel>

            <Panel label="Escrow — Xero · Logistics Chip · Tri-Party" accent={ACCENT} span={2}>
              <EscrowPipeline stage={stage} amounts={amounts} />
              <div style={{ display: 'flex', gap: 4, marginTop: 14, borderTop: `1px solid ${BORDER}`, paddingTop: 12, flexWrap: 'wrap' }}>
                {STAGES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`stage-${s.id}`}
                    onClick={() => setStage(s.id)}
                    style={{
                      flex: '1 1 100px',
                      padding: '8px 4px',
                      background: stage === s.id ? ACCENT : 'transparent',
                      border: `1px solid ${stage === s.id ? ACCENT : BORDER}`,
                      color: stage === s.id ? ACCENT_INK : TEXT_MUTED,
                      fontSize: 8,
                      fontWeight: 600,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      fontFamily: 'monospace',
                      transition: 'all 0.25s ease',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </Panel>
          </div>

          {/* Breakdown */}
          <div style={{ marginTop: 14 }}>
            <button
              data-testid="breakdown-toggle"
              onClick={() => setExpanded(!expanded)}
              style={{
                width: '100%',
                padding: '12px 18px',
                background: SURFACE,
                border: `1px solid ${BORDER}`,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontFamily: 'monospace',
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: TEXT_MUTED,
                borderRadius: 6,
              }}
            >
              <span>Full Settlement Breakdown</span>
              <span style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s ease', fontSize: 10, color: ACCENT }}>▾</span>
            </button>
            {expanded && (
              <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderTop: 'none', padding: '18px 22px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 28 }}>
                  <div>
                    {[
                      { l: 'Studio Price', v: price, c: TEXT_MUTED },
                      { l: `Markup (${markup}%)`, v: Math.round(price * markup / 100), c: TEXT_MUTED },
                      { l: `Tier (×${t.mult})`, v: galleryPrice - Math.round(price * (1 + markup / 100)), c: TEXT_MUTED },
                      { l: 'Gallery Sale Price', v: galleryPrice, c: TEXT, bold: true, line: true },
                      { l: `TrustFrame (${tfPct}%)`, v: -tfFee, c: ACCENT },
                      { l: 'Net Proceeds', v: net, c: TEXT, bold: true, line: true },
                      { l: 'Artist (50%)', v: artistShare, c: '#00d68f' },
                      { l: 'Gallery (50%)', v: galleryShare, c: '#4da6ff' },
                    ].map((r) => (
                      <div
                        key={r.l}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '7px 0',
                          borderTop: r.line ? `1px solid ${BORDER}` : 'none',
                          marginTop: r.line ? 4 : 0,
                        }}
                      >
                        <span style={{ fontSize: 10, color: TEXT_FAINT, fontFamily: 'monospace', letterSpacing: '0.08em' }}>{r.l}</span>
                        <span style={{ fontSize: 11, color: r.c, fontWeight: r.bold ? 700 : 400, fontFamily: 'monospace', letterSpacing: '0.04em' }}>
                          {r.v < 0 ? `−${fmt(Math.abs(r.v))}` : fmt(r.v)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{ border: `1px solid ${BORDER}`, padding: 18, background: BG_INK }}>
                    <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.22em', color: ACCENT, fontFamily: 'monospace', marginBottom: 12 }}>
                      ./ESCROW + XERO + CHIP
                    </div>
                    <p style={{ fontSize: 11, color: TEXT_MUTED, lineHeight: 1.7, fontFamily: 'sans-serif', margin: 0 }}>
                      Xero tracks {fmt(galleryPrice)} from collector invoice through to reconciliation. Logistics chip provides GPS from gallery to final location. All three parties — artist, gallery, TrustFrame — receive identical notifications at every stage.
                    </p>
                    <div style={{ marginTop: 14, padding: '10px 12px', background: SURFACE, border: `1px solid ${BORDER_FAINT}`, fontSize: 9, color: TEXT_FAINT, fontFamily: 'monospace', lineHeight: 1.6, letterSpacing: '0.08em' }}>
                      Artist + Gallery paid SIMULTANEOUSLY on VERIFIED. Xero RECONCILED. Chip deactivated. Zero asymmetry.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </PrivateLayout>
  );
}
